import { useQuery } from "@apollo/client";
import { BaseTable, Flexbox, IBaseTableProps } from "@sede-x/shell-ds-react-framework";
import { ColumnDef, getPaginationRowModel } from "@tanstack/react-table";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse, TTicketVolumeRevision } from "ticketing/ticketing.types";

type Response = GqlResponse<TTicketVolumeRevision[], "volumesOfTicketAtRevision">;

const query = loader("../../ticketing-graphql/volumesOfTicketAtRevision.graphql");

const transformData = (ticketRevisionVolumes: TTicketVolumeRevision[]) => {
  return ticketRevisionVolumes?.map(trm => ({
    ...trm,
    lastModifiedDate: new Date(trm.lastModifiedDate + "Z")
  }));
};

type RevisionsTicketVolumesProps = {
  ticketId: string;
  revisionNumber: string;
};

/***
 *
 */
const RevisionsTicketVolumes = ({ ticketId, revisionNumber }: RevisionsTicketVolumesProps) => {
  const [revisions, setRevisions] = useState<TTicketVolumeRevision[]>();
  const { loading, error } = useQuery<Response>(query, {
    fetchPolicy: "no-cache",
    variables: { ticketId, revisionNumber },
    onCompleted: data => setRevisions(transformData(data.volumesOfTicketAtRevision))
  });

  const columns: ColumnDef<TTicketVolumeRevision, string>[] = [
    {
      header: "Volume Id",
      accessorKey: "id"
    },
    {
      header: "Net Volume",
      accessorKey: "netVolume",
      cell: props => props.row.original.netVolume,
      size: 150
    },
    {
      header: "Gross Volume",
      accessorKey: "grossVolume",
      cell: props => props.row.original.grossVolume,
      size: 150
    },
    {
      header: "Unit of Measure",
      accessorKey: "unitOfMeasure.name"
    },
    {
      header: "Temperature",
      accessorKey: "temperature"
    },
    {
      header: "Temperature UOM",
      accessorKey: "temperatureUnitOfMeasure",
      size: 200
    },
    {
      header: "Updated At",
      accessorKey: "lastModifiedDate",
      cell: props => props.row.original.lastModifiedDate.toLocaleString(),
      size: 200
    },
    {
      header: "Updated By",
      accessorKey: "lastModifiedBy",
      cell: props =>
        [
          props.row.original.lastModifiedBy.lastName,
          props.row.original.lastModifiedBy.firstName
        ]
          .filter(Boolean)
          .join(", ")
    }
  ];
  const tableOptions: IBaseTableProps<TTicketVolumeRevision>["tableOptions"] = {
    defaultColumn: {
      minSize: 100,
      maxSize: 400
    },
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: true
  };

  return (
    <Flexbox
      flexDirection="column"
      style={{
        overflow: "auto"
      }}>
      {loading && <LoadingPanel />}
      {error && <ApolloErrorViewer error={error} />}
      <BaseTable
        columns={columns}
        data={revisions}
        tableOptions={tableOptions}
        style={{ width: "100%", tableLayout: "fixed" }}
        noFooter={true}
      />
    </Flexbox>
  );
};

export default RevisionsTicketVolumes;
